<template>
  <div v-if="!loading && plannerInfo.planner">
    <PlannerNavBar
      v-if="!isCustomPlanner"
      :is-daily="isDaily"
      :timeframe="timeframe"
      :time-label="plannerInfo.planner.timeLabel"
      :time="time"
      @date-change="handleDateChange"
      @update-months="updateMonths"
    />
    <PlannerActivityPicker
      v-if="!isCustomPlanner"
      :items="plannerInfo.planner.activities.items"
      :disable-select="plannerInfo.planner.activities.disableSelect"
      @update-activity="updateActivity"
    />

    <div
      v-if="activityMention && !isCustomPlanner"
      class="flex w-full mx-auto p-4 text-center justify-center mt-4 border-t border-b"
    >
      <span class="text-sm px-16">{{ activityMention }}</span>
    </div>

    <PlannerLabels
      v-if="showColors"
      :colors="plannerInfo.planner.colors"
    />
    <PlannerActivityDescription
      :selected-activity="selectedActivity"
      :descriptions="plannerInfo.planner.activities.descriptions"
    />

    <div class="px-2 pt-6 pb-10 md:p-12 md:max-w-[1360px] mx-auto" v-if="showDailyOrWeeklyPlanner">
      <PlannerDesktop class="hidden md:block w-full border-b border-t" :is-daily="isDaily" />
      <PlannerMobile class="block md:hidden w-full" :is-daily="isDaily"  />
    </div>
    <div class="w-full px-4 pb-3 pt-6" v-else>
      <PlannerByMonthsDesktop class="hidden md:block w-full" :is-daily="isDaily" />
      <PlannerByMonthsMobile class="block md:hidden w-full" :is-daily="isDaily" />
    </div>
    <Modal
      v-model="isContactModalOpen[props.pageId]"
      @close-dialog="handleCloseContactModal"
    >
      <template #body>
        <div class="relative flex flex-col bg-grey-300">
          <div class="w-full flex justify-end">
            <div class="px-1 mt-1 text-xl relative">
              <i
                tabindex="0"
                role="button"
                class="text-xl icon icon-cancel pointer black"
                @click="handleCloseContactModal"
              />
            </div>
          </div>
          <div class="flex justify-center items-center w-full bg-grey-300">
            <div class="p-2">
              {{ contactModalMessage }}
            </div>
          </div>
        </div>
      </template>
    </Modal>
  </div>
  <div
    v-else
    class="flex justify-center items-center h-full p-4"
  >
    <Loader />
  </div>
</template>

<script setup>
import { defineProps, ref, computed, provide, onMounted } from 'vue';

import PlannerNavBar from './PlannerNavBar.ce.vue';
import PlannerActivityPicker from './PlannerActivityPicker.ce.vue';
import PlannerDesktop from './PlannerDesktop.ce.vue';
import PlannerMobile from './PlannerMobile.ce.vue';
import PlannerByMonthsDesktop from './PlannerByMonthsDesktop.ce.vue';
import PlannerByMonthsMobile from './PlannerByMonthsMobile.ce.vue';
import PlannerLabels from './PlannerLabels.ce.vue';
import PlannerActivityDescription from './PlannerActivityDescription.ce.vue';
import Loader from '../Loader.ce.vue';
import Modal from '../Modal.ce.vue';

import { usePlannerStore } from '@/store/planner';
import { handleSubscription } from '@/utils/amplify';
import { computeSessionLabel } from '@/utils/helpers';

const plannerStore = usePlannerStore();

const props = defineProps({
  workspace: String,
  period: String,
  reservationPeriod: Number,
  isInternalSession: Boolean,
  codeSiteComptage: String,
  plannerMonthsToIndex: Number,
  timeframe: String,
  timezone: String,
  area: String,
  monthsToShow: Number,
  isCustomPlanner: Boolean,
  impersonateQuery: String,
  pageUuid: String,
  pageId: String,
  horanetSource: String,
  shortDescription: String,
  showColors: {
    type: Boolean,
    default: false
  },
  activityMention: String
});

const selectedActivity = ref('');

const plannerInfo = computed(() => {
  return plannerStore.plannerInfo ? plannerStore.plannerInfo[props.pageId] || {} : {};
});

const plannerHaveSessions = computed(() => {
  return (plannerInfo?.value?.planner?.columns || []).some(c => c.items.length > 0);
})

const loading = ref(false);

const time = ref(0);

const isContactModalOpen = ref({ });

const contactModalMessage = ref('');

const isDaily = computed(() => {
  return props.timeframe === 'daily';
});

const isPlannerByMonths = computed(() => {
  return props.timeframe === 'byMonths';
});

const showDailyOrWeeklyPlanner = computed(() => {
  return !isPlannerByMonths.value || props.isCustomPlanner;
});

const updateMonths = async (months) => {
  time.value = months;
  await getData();
};

const handleDateChange = async (direction) => {
  time.value += direction;
  await getData();
};

const updateActivity = async (activity) => {
  selectedActivity.value = activity;
};

const filteredPlannerInfo = computed(() => {
  if (!selectedActivity.value) {
    return plannerInfo.value;
  }

  const columns = plannerInfo.value.planner.columns.map(column => {
    let columnSessions = [...column.items];

    columnSessions = columnSessions.filter(session => {
      const label = computeSessionLabel(session);
      return label === selectedActivity.value;
    });

    return {
      ...column,
      items: columnSessions
    };
  }).filter(column => column && column.items.length > 0);

  return {
    ...plannerInfo.value,
    planner: {
      ...plannerInfo.value.planner,
      columns
    }
  };
});

const handleCloseContactModal = () => {
  isContactModalOpen.value = { [props.pageId]: false };
};

const openContactCenter = (message) => {
  isContactModalOpen.value = { ...isContactModalOpen.value, [props.pageId]: true };
  contactModalMessage.value = message;
};

const reddirectToTDA = (session) => {
  if (!session) {
    return;
  }

  const { activity_codes, codes, start_time, end_time } = session;
  const baseUrl = `${plannerInfo.value.funnelUrl}/products`;

  let url = `${baseUrl}?activities=${activity_codes.join(',')}&codes=${codes.join(',')}&start_time=${start_time}&end_time=${end_time}&workspace=${props.workspace}&page_url=${window.location.pathname}`;

  if (props.impersonateQuery) {
    url += `&impersonateQuery=${props.impersonateQuery}`;
  }

  window.location.href = url;
};

const getData = async () => {
  loading.value = true;
  if (props.isCustomPlanner) {
    await plannerStore.fetchCustomPlanner(props.pageId, props.pageUuid);
    loading.value = false;
    return;
  }

  await plannerStore.fetchSessions({ ...props, time: time.value });
  loading.value = false;
};

onMounted(async () => {
  plannerStore.setSiteInfo({
    workspace: props.workspace,
    codeSiteComptage: props.codeSiteComptage,
    isInternalSession: props.isInternalSession,
    horanetSource: props.horanetSource
  });
  await getData();

  if (!props.isCustomPlanner && !plannerStore.amplifySubscription) {
    await handleSubscription();
  }
});

provide('reddirectToTDA', reddirectToTDA);
provide('isDaily', isDaily);
provide('byMonths', isPlannerByMonths);
provide('isCustomPlanner', props.isCustomPlanner);
provide('plannerInfo', filteredPlannerInfo);
provide('plannerHaveSessions', plannerHaveSessions);
provide('openContactCenter', openContactCenter);
</script>
